import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { pickAddress, pickTranslation } from "../../helper/bookingApi";
import {
  formatClockTime,
  formatDayLabel,
  parseSlotDate,
} from "./bookingFormat";
import { BOOKING_FONT_FAMILY } from "./BookingPrimitives";

import { resolveImageUrl } from "./BookingTheme";
import "./TicketSuccessModal.scss";

/**
 * Build the deep-link / share URL list for each first-class share target.
 *
 * - WhatsApp: `wa.me` works on mobile (opens app) and desktop (opens
 *   web.whatsapp.com after the user is signed in).
 * - Viber: `viber://forward` only works when the Viber app is installed,
 *   so on desktops without Viber the click is silently ignored — the
 *   user can fall back to one of the other targets.
 * - Telegram: web share URL works on every platform.
 * - Instagram: there's no public share URL for plain text. We copy the
 *   message to the clipboard and open instagram.com so the user can
 *   paste into a DM.
 * - Facebook: the public sharer accepts a `u` (URL) param. The `quote`
 *   param is ignored for non-page URLs but harmless to include.
 * - Gmail: open the Gmail compose window pre-populated with subject /
 *   body. Falls back to a `mailto:` URI on devices without Gmail web.
 */
function buildShareTargets({ text, url, subject, calendarUrl }) {
  const t = encodeURIComponent(text);
  const u = encodeURIComponent(url || "");
  const s = encodeURIComponent(subject || "");
  const tu = url ? `${t}%0A${u}` : t;
  return [
    {
      id: "whatsapp",
      label: "WhatsApp",
      bg: "#25D366",
      icon: "lab la-whatsapp",
      href: `https://wa.me/?text=${tu}`,
    },
    {
      id: "viber",
      label: "Viber",
      bg: "#7360F2",
      icon: "lab la-viber",
      href: `viber://forward?text=${tu}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      bg: "#2AABEE",
      icon: "lab la-telegram-plane",
      href: `https://t.me/share/url?url=${u || t}&text=${t}`,
    },
    {
      id: "instagram",
      label: "Instagram",
      // Instagram has no plain-text web share URL. We copy + open IG.
      bgGradient:
        "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
      icon: "lab la-instagram",
      copyAndOpen: "https://www.instagram.com/",
    },
    {
      id: "facebook",
      label: "Facebook",
      bg: "#1877F2",
      icon: "lab la-facebook-f",
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
    },
    {
      id: "google-calendar",
      label: "Calendar",
      bg: "#D6D9DE",
      iconColor: "#111315",
      bordered: true,
      icon: "las la-calendar-plus",
      href: calendarUrl,
    },
    {
      id: "gmail",
      label: "Gmail",
      // Match the screenshot: light circle with envelope outline, brand-
      // neutral so it sits next to the colored chips without clashing.
      bg: "#F4F5F7",
      iconColor: "#1f2937",
      bordered: true,
      icon: "las la-envelope",
      href: `https://mail.google.com/mail/?view=cm&fs=1&su=${s}&body=${tu}`,
      mailto: `mailto:?subject=${s}&body=${tu}`,
    },
  ].filter((target) => target.href || target.copyAndOpen);
}

function formatGoogleCalendarDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function getServiceDurationMinutes(service) {
  const raw =
    service?.average_waiting ??
    service?.duration ??
    service?.waiting_time ??
    service?.category?.average_waiting ??
    service?.meta?.average_waiting;
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 30;
}

function buildGoogleCalendarUrl({
  title,
  startDate,
  durationMinutes,
  details,
  location,
}) {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return "";
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  const dates = `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Nomio booking",
    dates,
    details: details || "",
    location: location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function ShareChip({ target, onClick }) {
  return <button type="button" className="booking-success-share-chip" onClick={onClick}>
    <span style={{ background: target.bgGradient || target.bg, color: target.iconColor || "#fff" }}><i className={target.icon} aria-hidden="true" /></span>{target.label}
  </button>;
}

function buildMapUrl({ orgName, branchName, address }) {
  const query = [branchName || orgName, address].filter(Boolean).join(", ");
  if (!query) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function formatServiceNames(service, lang) {
  const services = Array.isArray(service)
    ? service.filter(Boolean)
    : service
      ? [service]
      : [];
  return services
    .map((item) => pickTranslation(item, lang))
    .filter(Boolean)
    .join(", ");
}

function buildShareText({
  t,
  code,
  orgName,
  branchName,
  dateLabel,
  timeLabel,
  address,
  mapUrl,
}) {
  const lines = [t("booking.successModal.share.heading", { code })];
  if (branchName) lines.push(`${t("booking.successModal.summary.branch")}: ${branchName}`);
  if (orgName && orgName !== branchName) {
    lines.push(`${t("booking.successModal.summary.organization")}: ${orgName}`);
  }
  if (dateLabel || timeLabel) {
    const dt = [dateLabel, timeLabel].filter(Boolean).join(", ");
    lines.push(`${t("booking.successModal.summary.datetime")}: ${dt}`);
  }
  if (address) {
    lines.push(`${t("booking.successModal.summary.address")}: ${address}`);
  }
  if (mapUrl) {
    lines.push(`${t("booking.successModal.summary.map")}: ${mapUrl}`);
  }
  return lines.join("\n");
}

export function TicketSuccessModal({
  isOpen,
  onClose,
  palette,
  lang,
  ticket,
  organization,
  branch,
  service,
  slot,
  isAuthenticated,
  ticketsHref,
  onNewBooking,
  onRegister,
  onCancel,
  holderName,
}) {
  const { t } = useTranslation();
  const dialogRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const keepBookingRef = useRef(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [shareState, setShareState] = useState("idle"); // idle | shared | copied | unsupported

  // Lock body scroll while open + handle Escape. Also reset transient
  // share feedback so reopening the modal always starts in the neutral
  // "Share" state.
  useEffect(() => {
    if (!isOpen) {
      setShareState("idle");
      setCancelError("");
      setConfirmCancel(false);
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && !cancelLoading) {
        if (confirmCancel) setConfirmCancel(false);
        else onClose?.();
      }
      if (e.key === "Tab") {
        const controls = dialogRef.current?.querySelectorAll('button:not(:disabled), a[href]');
        if (!controls?.length) return;
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      previousFocus?.focus?.();
    };
  }, [isOpen, onClose, confirmCancel, cancelLoading]);

  useEffect(() => {
    if (confirmCancel) keepBookingRef.current?.focus();
    else if (isOpen) cancelButtonRef.current?.focus();
  }, [confirmCancel, isOpen]);

  const code = ticket?.code || "";
  const orgName = organization ? pickTranslation(organization, lang) : "";
  const branchName = branch ? pickTranslation(branch, lang) : "";
  const serviceName = formatServiceNames(service, lang);
  const address = branch ? pickAddress(branch) : "";
  const mapUrl = useMemo(
    () => buildMapUrl({ orgName, branchName, address }),
    [orgName, branchName, address],
  );

  const slotDate = useMemo(() => {
    if (!slot) return null;
    if (slot.date instanceof Date) return slot.date;
    if (slot.raw) return parseSlotDate(slot.raw);
    return null;
  }, [slot]);

  // The ticket uses DD.MM.YYYY; share messages use the localized date.
  const dateLabel = slotDate ? [String(slotDate.getDate()).padStart(2, "0"), String(slotDate.getMonth() + 1).padStart(2, "0"), slotDate.getFullYear()].join(".") : "";
  const timeLabel = slotDate ? formatClockTime(slotDate) : "";
  const friendlyDateLabel = slotDate ? formatDayLabel(slotDate, lang, t) : "";

  /** Best-effort copy that falls back through three increasingly-old APIs. */
  const copyText = useCallback((payload) => {
    try {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(payload).then(
          () => true,
          () => false,
        );
      }
    } catch (_e) {
      /* fall through */
    }
    try {
      const el = document.createElement("textarea");
      el.value = payload;
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return Promise.resolve(ok);
    } catch (_e) {
      return Promise.resolve(false);
    }
  }, []);

  const shareText = useMemo(
    () =>
      buildShareText({
        t,
        code,
        orgName,
        branchName,
        dateLabel: friendlyDateLabel || dateLabel,
        timeLabel,
        address,
        mapUrl,
      }),
    [t, code, orgName, branchName, friendlyDateLabel, dateLabel, timeLabel, address, mapUrl],
  );

  const calendarUrl = useMemo(
    () =>
      buildGoogleCalendarUrl({
        title: [serviceName, branchName || orgName].filter(Boolean).join(" - "),
        startDate: slotDate,
        durationMinutes: getServiceDurationMinutes(service),
        details: shareText,
        location: [branchName || orgName, address].filter(Boolean).join(", "),
      }),
    [address, branchName, orgName, service, serviceName, shareText, slotDate],
  );

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${ticketsHref || ""}`
      : "";

  const shareTargets = useMemo(
    () =>
      buildShareTargets({
        text: shareText,
        url: shareUrl,
        subject: t("booking.successModal.share.title"),
        calendarUrl,
      }),
    [calendarUrl, shareText, shareUrl, t],
  );

  const handleShareTarget = useCallback(
    async (target) => {
      // Instagram has no direct text-share URL; the standard pattern is to
      // copy the message and route the user into the Instagram web app so
      // they can paste into a DM or story.
      if (target.copyAndOpen) {
        const ok = await copyText(shareUrl ? `${shareText}\n${shareUrl}` : shareText);
        setShareState(ok ? "copied" : "unsupported");
        try {
          window.open(target.copyAndOpen, "_blank", "noopener,noreferrer");
        } catch (_e) {
          /* popup blocked — copy state already set as feedback */
        }
        return;
      }
      // Gmail: try the web compose URL first, fall back to a `mailto:` URI.
      // Most desktops have a default mail handler even when the user isn't
      // on Gmail, so the action never silently fails.
      try {
        if (target.id === "gmail" && target.mailto) {
          const win = window.open(
            target.href,
            "_blank",
            "noopener,noreferrer",
          );
          if (!win) window.location.href = target.mailto;
        } else if (target.href) {
          window.open(target.href, "_blank", "noopener,noreferrer");
        }
        setShareState("shared");
      } catch (_e) {
        setShareState("unsupported");
      }
    },
    [copyText, shareText, shareUrl],
  );

  // Auto-revert "copied" / "shared" feedback after 2.5s.
  useEffect(() => {
    if (shareState === "idle") return undefined;
    const id = setTimeout(() => setShareState("idle"), 2500);
    return () => clearTimeout(id);
  }, [shareState]);

  if (!isOpen) return null;

  // Compact toast-style feedback rendered next to the share heading.
  const shareFeedback =
    shareState === "copied"
      ? t("booking.successModal.share.copied")
      : shareState === "shared"
        ? t("booking.successModal.share.opened")
        : shareState === "unsupported"
          ? t("booking.successModal.share.unsupported")
          : "";

  const handleCancel = async () => {
    if (!confirmCancel || cancelLoading || !onCancel) return;
    setCancelLoading(true);
    setCancelError("");
    try {
      await onCancel();
      setConfirmCancel(false);
    }
    catch (error) { setCancelError(error?.message || t("booking.common.errorGeneric")); }
    finally { setCancelLoading(false); }
  };
  const orgImage = resolveImageUrl(organization?.image_path);
  const showRegister = !isAuthenticated && typeof onRegister === "function";
  const dismiss = () => {
    if (cancelLoading) return;
    if (confirmCancel) setConfirmCancel(false);
    else onClose?.();
  };
  const modal = (
    <div className="booking-success-backdrop" onClick={dismiss}>
      <div ref={dialogRef} className={`booking-success-card${confirmCancel ? " booking-success-confirm" : ""}`} data-theme={palette.surface === "#191C1F" ? "dark" : "light"}
        role={confirmCancel ? "alertdialog" : "dialog"} aria-modal="true" aria-labelledby={confirmCancel ? "booking-cancel-title" : "booking-success-title"}
        aria-describedby={confirmCancel ? "booking-cancel-description" : undefined} aria-busy={cancelLoading}
        style={{ fontFamily: BOOKING_FONT_FAMILY }} onClick={(event) => event.stopPropagation()}>
        <button type="button" className="booking-success-close" onClick={dismiss} disabled={cancelLoading} aria-label={t("booking.successModal.close")} autoFocus><i className="las la-times" aria-hidden="true" /></button>
        {confirmCancel ? <>
          <span className="booking-success-confirm-icon" aria-hidden="true"><i className="las la-calendar-times" /></span>
          <h3 id="booking-cancel-title">{t("booking.tickets.cancelModal.title")}</h3>
          <p id="booking-cancel-description">{t("booking.tickets.cancelModal.message")}</p>
          {cancelError && <p role="alert" className="booking-success-error">{cancelError}</p>}
          <div className="booking-success-actions">
            <button ref={keepBookingRef} type="button" className="booking-success-keep" disabled={cancelLoading} onClick={() => setConfirmCancel(false)}>{t("booking.common.back")}</button>
            <button type="button" disabled={cancelLoading} onClick={handleCancel}>{t(cancelLoading ? "booking.tickets.cancelModal.cancelling" : "booking.tickets.cancelModal.confirm")}</button>
          </div>
        </> : <>
        <div className="booking-success-hero">
          <span className="booking-success-check" aria-hidden="true"><i className="las la-check" /></span>
          <h3 id="booking-success-title">{t("booking.successModal.title")}</h3>
        </div>
        <div className="booking-success-info">
          <div className="booking-success-organization">
            <div><h4>{orgName || branchName || "\u2014"}</h4>{branchName && <p>{branchName}</p>}</div>
            {orgImage && <img src={orgImage} alt={orgName} />}
          </div>
          <div className="booking-success-details">
            {serviceName && <h4>{serviceName}</h4>}
            <div className="booking-success-summary">
              <div><span><i className="las la-calendar" aria-hidden="true" />{t("booking.successModal.summary.date")}</span><strong>{dateLabel || "\u2014"}</strong></div>
              <div><span><i className="las la-clock" aria-hidden="true" />{t("booking.successModal.summary.time")}</span><strong>{timeLabel || "\u2014"}</strong></div>
              <div className="booking-success-address"><span><span><i className="las la-map-marker" aria-hidden="true" />{t("booking.successModal.summary.address")}</span>
                {mapUrl && <a href={mapUrl} target="_blank" rel="noopener noreferrer"><i className="las la-map" aria-hidden="true" />{t("booking.successModal.summary.map")}</a>}</span><strong>{address || "\u2014"}</strong></div>
            </div>
            <div className="booking-success-code-row">
              <div><span className="booking-success-label">{t("booking.successModal.codeLabel")}</span><div className="booking-success-code">{code || "\u2014"}</div></div>
              {holderName && <div className="booking-success-holder"><span className="booking-success-label">{t("booking.successModal.holderLabel")}</span><p>{holderName}</p></div>}
              {onCancel && <button ref={cancelButtonRef} className="booking-success-cancel" type="button" onClick={() => { setCancelError(""); setConfirmCancel(true); }}>{t("booking.tickets.card.cancelBtn")}</button>}
            </div>
            {cancelError && <p role="alert" className="booking-success-error">{cancelError}</p>}
          </div>
        </div>
        <div className="booking-success-share">
          <div className="booking-success-share-head"><h4>{t("booking.successModal.share.via")}</h4><span role="status">{shareFeedback}</span></div>
          <div className="booking-success-share-grid">{shareTargets.filter(target => target.id !== "gmail").map(target => <ShareChip key={target.id} target={target} onClick={() => handleShareTarget(target)} />)}</div>
        </div>
        {showRegister && <div className="booking-success-account"><h4>{t("booking.accountPrompt.title")}</h4><p>{t("booking.accountPrompt.text")}</p></div>}
        <div className="booking-success-actions">
          {showRegister ? <button type="button" onClick={() => { onClose?.(); onRegister(); }}>{t("booking.accountPrompt.button")}</button>
            : isAuthenticated && ticketsHref ? <Link to={ticketsHref} onClick={onClose}>{t("booking.successModal.actions.viewTickets")}</Link>
            : <button type="button" onClick={() => { onClose?.(); onNewBooking?.(); }}>{t("booking.successModal.actions.newBooking")}</button>}
        </div>
        </>}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}

export default TicketSuccessModal;
